import { AttachmentCategory, type AttachmentSelectDto, AttachmentType } from '@@types/api.types';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  type AttachmentTargetType,
  type BufferedAttachment,
  useInnovatorClearTechnologyFeaturedImage,
  useInnovatorCreateAttachmentUrl,
  useInnovatorDeleteAttachment,
  useInnovatorFeatureTechnologyImage,
  useInnovatorUpdateAttachment,
  useInnovatorUploadAttachmentFile,
} from '@queries/attachments.query';
import { type AttachmentFormData, normalizeAttachmentUrl } from '@schemas/attachment.schema';
import { getAttachmentToastMessage } from '@utils/attachment-toast.util';
import { getAttachmentCategory, getUpdateErrorMessage } from './attachments-modal.util';
import { type TechnologyAttachmentEditorKind, getTechnologyAttachmentEditorKind } from './technology-attachments.util';
import { validateTechnologyImageFile } from './technology-image.util';

type UseAttachmentModalSubmitParams = {
  attachmentToEdit: AttachmentSelectDto | null;
  bufferedAttachmentToEdit?: BufferedAttachment | null;
  category: AttachmentCategory;
  currentFeaturedTechnologyImageId?: string;
  editorKind: TechnologyAttachmentEditorKind;
  featured: boolean;
  formAttachmentType: AttachmentType;
  isBufferMode: boolean;
  isEdit: boolean;
  isEditingExisting: boolean;
  onBufferRemove?: (clientId: string) => void;
  onBufferSave?: (data: BufferedAttachment) => void;
  onClose: () => void;
  targetId?: string;
  targetType: AttachmentTargetType;
};

type UseAttachmentModalSubmitResult = {
  handleDelete: () => Promise<void>;
  isDeletePending: boolean;
  isPending: boolean;
  onSubmit: (value: AttachmentFormData) => Promise<void>;
};

export const useAttachmentModalSubmit = (params: UseAttachmentModalSubmitParams): UseAttachmentModalSubmitResult => {
  const {
    attachmentToEdit,
    bufferedAttachmentToEdit,
    category,
    currentFeaturedTechnologyImageId,
    editorKind,
    featured,
    formAttachmentType,
    isBufferMode,
    isEdit,
    isEditingExisting,
    onBufferRemove,
    onBufferSave,
    onClose,
    targetId,
    targetType,
  } = params;

  const { mutateAsync: clearTechnologyFeaturedImage, isPending: isClearFeaturedPending } =
    useInnovatorClearTechnologyFeaturedImage();
  const { mutateAsync: createAttachmentUrl, isPending: isCreateUrlPending } = useInnovatorCreateAttachmentUrl();
  const { mutateAsync: deleteAttachment, isPending: isDeletePending } = useInnovatorDeleteAttachment();
  const { mutateAsync: featureTechnologyImage, isPending: isFeaturePending } = useInnovatorFeatureTechnologyImage();
  const { mutateAsync: updateAttachment, isPending: isUpdatePending } = useInnovatorUpdateAttachment();
  const { mutateAsync: uploadAttachmentFile, isPending: isUploadPending } = useInnovatorUploadAttachmentFile();

  const isPending =
    !isBufferMode &&
    (isClearFeaturedPending ||
      isCreateUrlPending ||
      isDeletePending ||
      isFeaturePending ||
      isUpdatePending ||
      isUploadPending);

  const runRollbackAction = async (action: () => Promise<void>): Promise<boolean> => {
    try {
      await action();
      return false;
    } catch {
      return true;
    }
  };

  const updateTechnologyImageFeaturedState = async (params: {
    attachmentId: string;
    featured: boolean;
    isCurrentlyFeatured: boolean;
    technologyId: string;
  }): Promise<boolean> => {
    const { attachmentId, featured, isCurrentlyFeatured, technologyId } = params;

    if (featured === isCurrentlyFeatured) {
      return false;
    }

    try {
      if (featured) {
        await featureTechnologyImage({
          attachmentId,
          technologyId,
        });
      } else {
        await clearTechnologyFeaturedImage({
          attachmentId,
          technologyId,
        });
      }
    } catch {
      return true;
    }

    return false;
  };

  const onSubmit = async (value: AttachmentFormData): Promise<void> => {
    const attachmentCategory = getAttachmentCategory({ category, kind: editorKind });
    const file = value.file ?? bufferedAttachmentToEdit?.file;
    const isImageAttachment = attachmentCategory === AttachmentCategory.TECHNOLOGY_IMAGE;
    const normalizedUrl =
      formAttachmentType === AttachmentType.URL && value.url ? normalizeAttachmentUrl(value.url) : undefined;

    if (isBufferMode && onBufferSave) {
      const clientId = bufferedAttachmentToEdit?.clientId ?? crypto.randomUUID();

      onBufferSave({
        category: attachmentCategory,
        clientId,
        contentType: file?.type,
        description: value.description,
        featured: isImageAttachment ? featured : undefined,
        file,
        size: file?.size,
        title: value.title,
        type: formAttachmentType,
        url: formAttachmentType === AttachmentType.URL ? normalizedUrl : undefined,
      });

      onClose();
      return;
    }

    if (!targetId) {
      return;
    }

    try {
      if (isEditingExisting && attachmentToEdit) {
        if (isImageAttachment && value.file) {
          const validationError = validateTechnologyImageFile(value.file);

          if (validationError) {
            errorToast({ description: validationError });
            return;
          }

          let replacementAttachment: AttachmentSelectDto | undefined;
          let shouldRestoreFeaturedImage = false;

          try {
            replacementAttachment = await uploadAttachmentFile({
              category: attachmentCategory,
              description: value.description,
              file: value.file,
              targetId,
              targetType,
              title: value.title,
            });

            if (featured) {
              await featureTechnologyImage({
                attachmentId: replacementAttachment.id,
                technologyId: targetId,
              });
              shouldRestoreFeaturedImage = Boolean(attachmentToEdit.featured);
            }

            await deleteAttachment({
              category: attachmentCategory,
              id: attachmentToEdit.id,
              targetId,
              targetType,
            });
          } catch {
            let hasRollbackError = false;

            if (replacementAttachment) {
              const replacementAttachmentId = replacementAttachment.id;

              if (shouldRestoreFeaturedImage) {
                hasRollbackError =
                  (await runRollbackAction(async () => {
                    await featureTechnologyImage({
                      attachmentId: attachmentToEdit.id,
                      technologyId: targetId,
                    });
                  })) || hasRollbackError;
              }

              hasRollbackError =
                (await runRollbackAction(async () => {
                  await deleteAttachment({
                    category: attachmentCategory,
                    id: replacementAttachmentId,
                    targetId,
                    targetType,
                  });
                })) || hasRollbackError;
            }

            errorToast({
              description: hasRollbackError
                ? 'Failed to update technology image. Some cleanup steps may not have completed.'
                : getUpdateErrorMessage(true),
            });
            return;
          }

          successToast({
            description: getAttachmentToastMessage({
              action: 'update',
              isImage: true,
              success: true,
            }),
          });
          onClose();
          return;
        }

        await updateAttachment({
          category: attachmentCategory,
          description: value.description,
          id: attachmentToEdit.id,
          targetId,
          targetType,
          title: value.title,
          url: formAttachmentType === AttachmentType.URL ? normalizedUrl : undefined,
        });

        const featureError = isImageAttachment
          ? await updateTechnologyImageFeaturedState({
              attachmentId: attachmentToEdit.id,
              featured,
              isCurrentlyFeatured: Boolean(attachmentToEdit.featured),
              technologyId: targetId,
            })
          : false;

        successToast({
          description: getAttachmentToastMessage({
            action: 'update',
            featureError,
            isImage: isImageAttachment,
            success: true,
          }),
        });
        onClose();
        return;
      }

      if (formAttachmentType === AttachmentType.URL) {
        await createAttachmentUrl({
          category: attachmentCategory,
          description: value.description,
          targetId,
          targetType,
          title: value.title,
          url: normalizedUrl ?? '',
        });

        successToast({ description: 'Attachment added successfully.' });
        onClose();
        return;
      }

      if (!file) {
        errorToast({ description: 'Please select a file to upload.' });
        return;
      }

      if (isImageAttachment) {
        const validationError = validateTechnologyImageFile(file);

        if (validationError) {
          errorToast({ description: validationError });
          return;
        }
      }

      const uploadedAttachment = await uploadAttachmentFile({
        category: attachmentCategory,
        description: value.description,
        file,
        targetId,
        targetType,
        title: value.title,
      });

      let featureError = false;

      if (isImageAttachment && featured) {
        try {
          await featureTechnologyImage({
            attachmentId: uploadedAttachment.id,
            technologyId: targetId,
          });
        } catch {
          featureError = true;
        }
      } else if (isImageAttachment && currentFeaturedTechnologyImageId) {
        try {
          await featureTechnologyImage({
            attachmentId: currentFeaturedTechnologyImageId,
            technologyId: targetId,
          });
        } catch {
          featureError = true;
        }
      }

      successToast({
        description: getAttachmentToastMessage({
          action: 'create',
          featureError,
          isImage: isImageAttachment,
          success: true,
        }),
      });
      onClose();
    } catch {
      errorToast({
        description: getAttachmentToastMessage({
          action: isEdit ? 'update' : 'create',
          isImage: isImageAttachment,
          success: false,
        }),
      });
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (isBufferMode && onBufferRemove && bufferedAttachmentToEdit) {
      onBufferRemove(bufferedAttachmentToEdit.clientId);
      onClose();
      return;
    }

    if (!attachmentToEdit || !targetId) {
      return;
    }

    const attachmentCategory = getAttachmentCategory({
      category,
      kind: getTechnologyAttachmentEditorKind(attachmentToEdit),
    });

    try {
      await deleteAttachment({
        category: attachmentCategory,
        id: attachmentToEdit.id,
        targetId,
        targetType,
      });
      successToast({
        description: getAttachmentToastMessage({
          action: 'delete',
          isImage: attachmentCategory === AttachmentCategory.TECHNOLOGY_IMAGE,
          success: true,
        }),
      });
      onClose();
    } catch {
      errorToast({
        description: getAttachmentToastMessage({
          action: 'delete',
          isImage: attachmentCategory === AttachmentCategory.TECHNOLOGY_IMAGE,
          success: false,
        }),
      });
    }
  };

  return {
    handleDelete,
    isDeletePending,
    isPending,
    onSubmit,
  };
};
